var __decorate = (this && this.__decorate) || function (decorators, target, key, desc) {
    var c = arguments.length, r = c < 3 ? target : desc === null ? desc = Object.getOwnPropertyDescriptor(target, key) : desc, d;
    if (typeof Reflect === "object" && typeof Reflect.decorate === "function") r = Reflect.decorate(decorators, target, key, desc);
    else for (var i = decorators.length - 1; i >= 0; i--) if (d = decorators[i]) r = (c < 3 ? d(r) : c > 3 ? d(target, key, r) : d(target, key)) || r;
    return c > 3 && r && Object.defineProperty(target, key, r), r;
};
import { html, KompisElement, property } from '@inter-ikea-kompis/web-component';
import { ThemeBreakpointIndexEnum } from '@inter-ikea-kompis/enums';
import { ThemeBreakpointObserver } from '@inter-ikea-kompis/themes';
import { ProductUtility } from '@inter-ikea-kompis/utilities';
import { ButtonSizeEnum } from '@inter-ikea-kompis/component-button';
import { MiniPriceAlignmentEnum } from '@inter-ikea-kompis/component-mini-price';
import { MiniPrice } from '@inter-ikea-kompis/component-mini-price';
import { Button as SkapaButton, IconButton as SkapaIconButton } from '@ingka/button-webc/passive.js';
import { arrowRight, informationCircle, registerIcon } from '@ingka/icon-store';
import { Icon as SkapaIcon } from '@ingka/icon-webc/passive.js';
import MiniConfigurationSummaryIconTypeEnum from '../../enums/MiniConfigurationSummaryIconTypeEnum.js';
import MiniConfigurationSummaryStyle from './MiniConfigurationSummaryStyle.js';
/**
 * Mini Configuration Summary View.
 *
 * @event modalOpen Triggered when the user clicks on the eco fee link
 * @event modalClose Triggered when the user clicks on the sheet close button.
 * @event openWhatsIncluded Triggered when the user clicks on the whatsIncluded link or whatsIncluded icon.
 * @event next When next button is clicked.
 */
export default class MiniConfigurationSummary extends KompisElement {
    constructor() {
        super(...arguments);
        // Localized information. Deprecated - use "translations" and "settings" instead.
        this.localizedInformation = null;
        // The translations retrieved from translations service.
        this.translations = null;
        // The settings retrieved from settings service.
        this.settings = null;
        // Set price to be animated
        this.animatedPrice = false;
        // Disables button
        this.disabled = false;
        // Icon type
        this.iconType = MiniConfigurationSummaryIconTypeEnum.primaryInverted;
        // Hide whatsIncluded icon
        this.hideWhatsIncludedIcon = false;
        // Button size.
        this.buttonSize = ButtonSizeEnum.medium;
        // Button label
        this.buttonLabel = null;
        // Hide button icon
        this.hideButtonIcon = false;
        // Disables links. This attribute should be set to "true" when in a Kiosk.
        this.disableLinks = false;
        // Renders the loading style
        this.loading = false;
        // Show a modal.
        this.visibleModal = null;
        this.breakpointObserver = new ThemeBreakpointObserver(() => this.requestUpdate());
    }
    /**
     * Renders view.
     *
     * @returns Template.
     */
    render() {
        // We want translations and settings to be required, but since all props to provide translations and settings are optional, this check is needed until localizedInformation is removed.
        if (!this.shoppingProducts || !this.hasTranslationsAndSettings()) {
            return null;
        }
        return html `
			<style>
				${MiniConfigurationSummaryStyle.getStyle({
            theme: this.theme
        })}
			</style>
			<div class="container">
				${this.getPrice()}
				<div class="divider"></div>
				${this.getNextButton()}
			</div>
		`;
    }
    disconnectedCallback() {
        super.disconnectedCallback();
        this.breakpointObserver.disconnect();
    }
    async connectedCallback() {
        registerIcon(arrowRight);
        registerIcon(informationCircle);
        this.customElements.define('skapa-button', SkapaButton);
        this.customElements.define('skapa-icon-button', SkapaIconButton);
        this.customElements.define('skapa-icon', SkapaIcon);
        this.customElements.define('kompis-mini-price', MiniPrice);
        super.connectedCallback();
    }
    shouldUpdate(changedProperties) {
        if (changedProperties.has('theme')) {
            this.breakpointObserver.observe(this.theme);
        }
        return true;
    }
    hasTranslationsAndSettings() {
        return (!!this.translations && !!this.settings) || !!this.localizedInformation;
    }
    getTranslations() {
        return this.translations
            ? this.translations
            : this.localizedInformation?.translations;
    }
    /**
     * Returns mini price.
     *
     * @returns Html.
     */
    getPrice() {
        return html `
			<kompis-mini-price
				.theme="${this.theme}"
				.disableLinks="${this.disableLinks}"
				.shoppingProducts="${this.shoppingProducts}"
				.localizedInformation="${this.localizedInformation}"
				.settings="${this.settings}"
				.translations="${this.translations}"
				.animatedPrice="${this.animatedPrice}"
				.align="${MiniPriceAlignmentEnum.right}"
				.showEcoFee="${true}"
				.visibleModal="${this.visibleModal}"
				@modalOpen="${(event) => this.dispatchEvent(new CustomEvent('modalOpen', { bubbles: true, detail: event.detail }))}"
				@modalClose="${(event) => this.dispatchEvent(new CustomEvent('modalClose', { bubbles: true, detail: event.detail }))}"
			>
				${this.getWhatsIncludedIcon()}
			</kompis-mini-price>
		`;
    }
    /**
     * Returns the label for the checkout button.
     *
     * @returns Label to checkout button.
     */
    getNextButtonLabel() {
        return this.buttonLabel || this.getTranslations().buttonNextStep || '';
    }
    /**
     * Returns the checkout button.
     *
     * @returns Button to checkout.
     */
    getNextButton() {
        const breakpointIndex = this.breakpointObserver.getBreakpointIndex();
        const emphasisedVariant = 'emphasised';
        if (breakpointIndex !== null && breakpointIndex <= ThemeBreakpointIndexEnum.small) {
            return html `
				<skapa-icon-button
					variant="${emphasisedVariant}"
					size="${this.buttonSize === ButtonSizeEnum.small ? 'small' : 'medium'}"
					?disabled="${this.disabled}"
					?loading="${this.loading}"
					loadingSrText="${this.getTranslations().loading}"
					@click="${() => this.dispatchEvent(new CustomEvent('next', { bubbles: true }))}"
				>
					<skapa-icon slot="icon" flip-rtl icon="arrow-right"></skapa-icon>
					${this.getNextButtonLabel()}
				</skapa-icon-button>
			`;
        }
        return html `
			<skapa-button
				variant="${emphasisedVariant}"
				size="${this.buttonSize === ButtonSizeEnum.small ? 'small' : 'medium'}"
				?disabled="${this.disabled}"
				?loading="${this.loading}"
				loadingSrText="${this.getTranslations().loading}"
				icon-position="trailing"
				@click="${() => this.dispatchEvent(new CustomEvent('next', { bubbles: true }))}"
			>
				${this.hideButtonIcon
            ? null
            : html `<skapa-icon
							slot="icon"
							icon="arrow-right"
							?flip-rtl="${this.hideButtonIcon ? false : true}"
						></skapa-icon>`}
				${this.getNextButtonLabel()}
			</skapa-button>
		`;
    }
    /**
     * @param shoppingProducts ShoppingProducts.
     * @returns ShoppingItems.
     */
    getShoppingItems(shoppingProducts) {
        const validShoppingProducts = shoppingProducts.filter((shoppingProduct) => !!shoppingProduct.product.content);
        return ProductUtility.getShoppingItems(validShoppingProducts);
    }
    /**
     * Callback for onOpenWhatsIncluded event.
     */
    onOpenWhatsIncluded() {
        if (!this.disabled) {
            this.dispatchEvent(new CustomEvent('openWhatsIncluded', {
                bubbles: true,
                detail: {
                    shoppingItems: this.getShoppingItems(this.shoppingProducts),
                    triggerElement: this.queryWhatsIncludedButton()
                }
            }));
        }
    }
    queryWhatsIncludedButton() {
        return (this.shadowRoot?.querySelector('#whatsIncluded')?.shadowRoot?.querySelector('button') ?? null);
    }
    /**
     * @returns WhatsIncludedIcon.
     */
    getWhatsIncludedIcon() {
        if (this.hideWhatsIncludedIcon) {
            return null;
        }
        const buttonVariant = this.iconType === MiniConfigurationSummaryIconTypeEnum.primaryInverted
            ? 'primary'
            : 'tertiary';
        return html `
			<skapa-icon-button
				id="whatsIncluded"
				?disabled="${this.disabled}"
				variant="${buttonVariant}"
				?inverse="${this.iconType === MiniConfigurationSummaryIconTypeEnum.primaryInverted}"
				size="xsmall"
				@click="${!this.disabled ? this.onOpenWhatsIncluded.bind(this) : null}"
			>
				<skapa-icon slot="icon" icon="information-circle"></skapa-icon>
				${this.getTranslations().miniConfigurationSummaryAriaLabelWhatsIncluded}
			</skapa-icon-button>
		`;
    }
}
__decorate([
    property({ type: Object })
], MiniConfigurationSummary.prototype, "shoppingProducts", void 0);
__decorate([
    property({ type: Object })
], MiniConfigurationSummary.prototype, "localizedInformation", void 0);
__decorate([
    property({ type: Object })
], MiniConfigurationSummary.prototype, "translations", void 0);
__decorate([
    property({ type: Object })
], MiniConfigurationSummary.prototype, "settings", void 0);
__decorate([
    property({ type: Object, required: true })
], MiniConfigurationSummary.prototype, "theme", void 0);
__decorate([
    property({ type: Boolean })
], MiniConfigurationSummary.prototype, "animatedPrice", void 0);
__decorate([
    property({ type: Boolean })
], MiniConfigurationSummary.prototype, "disabled", void 0);
__decorate([
    property({ type: String })
], MiniConfigurationSummary.prototype, "iconType", void 0);
__decorate([
    property({ type: Boolean })
], MiniConfigurationSummary.prototype, "hideWhatsIncludedIcon", void 0);
__decorate([
    property({ type: String })
], MiniConfigurationSummary.prototype, "buttonSize", void 0);
__decorate([
    property({ type: String })
], MiniConfigurationSummary.prototype, "buttonLabel", void 0);
__decorate([
    property({ type: Boolean })
], MiniConfigurationSummary.prototype, "hideButtonIcon", void 0);
__decorate([
    property({ type: Boolean })
], MiniConfigurationSummary.prototype, "disableLinks", void 0);
__decorate([
    property({ type: Boolean })
], MiniConfigurationSummary.prototype, "loading", void 0);
__decorate([
    property({ type: String })
], MiniConfigurationSummary.prototype, "visibleModal", void 0);
//# sourceMappingURL=MiniConfigurationSummary.js.map